import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import { communitiesPaths } from "@circle-react/helpers/routerPathHelpers";
import { PlatformMenuItemLinkV3 } from "./PlatformMenuItemLinkV3";
import { usePlatformMenuV3 } from "./usePlatformMenuV3";
import {
  useIsEoYRecapEnabled,
  useIsEoYRecapEnabledForMembers,
} from "@/react/components/SettingsApp/EoYRecap/hooks/useIsEoYRecapEnabled";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useGetSidebarLinkClass } from "@circle-react/components/Spaces/SidebarV2/CommunityMenu/SpaceList/useGetSidebarLinkClass";
import { Icon } from "@circle-react-shared/Icon";

export interface PlatformMenuV3Props {
  isDisabled?: boolean;
  isFeedLinkActive?: boolean;
}

export const PlatformMenuV3 = ({
  isDisabled = false,
  isFeedLinkActive = false,
}: PlatformMenuV3Props) => {
  const { shouldShowOnboarding, hideOnboardingModal } = usePlatformMenuV3();
  const { currentCommunity, currentCommunityMember } = usePunditUserContext();
  const { home_page_enabled: shouldShowFeed } = currentCommunity ?? {};
  const shouldShowRecap2025 = useIsEoYRecapEnabled();
  const shouldShowRecap2025ForMembers = useIsEoYRecapEnabledForMembers();
  const { getActiveInactiveClasses } = useGetSidebarLinkClass();

  if (!shouldShowOnboarding && !shouldShowFeed) {
    return null;
  }

  return (
    <nav aria-label={t("secondary_navigation_bar")}>
      <ul className="bg-c-sidebar mt-4 flex list-none flex-col gap-y-1 px-3 lg:mt-6 lg:px-6">
        {shouldShowOnboarding && (
          <li className="relative">
            <PlatformMenuItemLinkV3
              text={t("getting_started")}
              linkTo={communitiesPaths.onboarding()}
              icon="20-todo-list-v3"
              isDisabled={isDisabled}
              onClose={async () => {
                await hideOnboardingModal.show();
              }}
            />
          </li>
        )}
        {shouldShowFeed && (
          <li className="relative">
            <PlatformMenuItemLinkV3
              text={t("feed")}
              linkTo={communitiesPaths.feed()}
              icon="20-feed-v3"
              isDisabled={isDisabled}
              isActive={isFeedLinkActive}
            />
          </li>
        )}
        {shouldShowRecap2025 && isCommunityAdmin(currentCommunityMember) && (
          <li className="relative">
            <PlatformMenuItemLinkV3
              text={t("eoy_recap_2025.community_recap")}
              linkTo={communitiesPaths.recap2025()}
              icon="16-link"
            />
          </li>
        )}
        {shouldShowRecap2025ForMembers && (
          <li className="relative">
            <a
              href={communitiesPaths.recap2025ForMembersAndNonMembers()}
              target="_blank"
              rel="noopener noreferrer"
              title={t("eoy_recap_2025.community_recap")}
              className={classNames(
                "h-8.5 flex w-full items-center gap-2 !rounded-lg px-4 py-1.5 transition-colors duration-200",
                getActiveInactiveClasses(false),
              )}
            >
              <Icon type="16-link" size={20} />
              <h4 className="text-xs-plus max-w-full truncate font-medium text-current">
                {t("eoy_recap_2025.community_recap")}
              </h4>
            </a>
          </li>
        )}
      </ul>
    </nav>
  );
};
